import { useState, useEffect, useCallback } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { SocketProvider, useSocket } from './context/SocketContext';
import Navbar from './components/Layout/Navbar';
import BottomNav from './components/Layout/BottomNav';
import Login from './components/Auth/Login';
import Register from './components/Auth/Register';
import HomePage from './pages/HomePage';
import DiscoverPage from './pages/DiscoverPage';
import MatchesPage from './pages/MatchesPage';
import ChatPage from './pages/ChatPage';
import ProfilePage from './pages/ProfilePage';
import ProfileEdit from './components/Profile/ProfileEdit';
import AdminPage from './pages/AdminPage';
import WallPage from './pages/WallPage';
import VoiceOnboardingPage from './pages/VoiceOnboardingPage';
import { ToastProvider } from './context/ToastContext';
import api from './api/axios';

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="loading-spinner" style={{ height: '100vh' }}>
        <div className="spinner" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return children;
}

function AdminRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="loading-spinner" style={{ height: '100vh' }}>
        <div className="spinner" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (user.is_admin !== 1) {
    return <Navigate to="/discover" replace />;
  }

  return children;
}

function AppLayout({ children }) {
  const { user } = useAuth();
  const { incomingMessage, unreadCount: socketUnread } = useSocket();
  const [polledUnread, setPolledUnread] = useState(0);

  const fetchUnread = useCallback(async () => {
    try {
      const res = await api.get('/users/unread');
      setPolledUnread(res.data.total);
    } catch {
      // ignore
    }
  }, []);

  // Fetch unread on mount and every 60s as a safety-net fallback
  useEffect(() => {
    if (!user) return;
    fetchUnread();
    const interval = setInterval(fetchUnread, 60000);
    return () => clearInterval(interval);
  }, [user, fetchUnread]);

  // Re-fetch when a new message arrives via socket (fallback path)
  useEffect(() => {
    if (incomingMessage && incomingMessage.id) {
      fetchUnread();
    }
  }, [incomingMessage, fetchUnread]);

  // Prefer socket-pushed count when available; fall back to REST poll
  const unreadCount = socketUnread !== null ? socketUnread : polledUnread;

  if (!user) return children;

  return (
    <div className="app-layout">
      <Navbar />
      <div className="app-content">{children}</div>
      <BottomNav unreadCount={unreadCount} />
    </div>
  );
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<HomePage />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route
        path="/discover"
        element={
          <ProtectedRoute>
            <AppLayout>
              <DiscoverPage />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/wall"
        element={
          <ProtectedRoute>
            <AppLayout>
              <WallPage />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/matches"
        element={
          <ProtectedRoute>
            <AppLayout>
              <MatchesPage />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/chat/:matchId"
        element={
          <ProtectedRoute>
            <AppLayout>
              <ChatPage />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/onboarding/voice"
        element={
          <ProtectedRoute>
            <AppLayout>
              <VoiceOnboardingPage />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <AppLayout>
              <ProfilePage />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile/edit"
        element={
          <ProtectedRoute>
            <AppLayout>
              <ProfileEdit />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin"
        element={
          <AdminRoute>
            <AppLayout>
              <AdminPage />
            </AppLayout>
          </AdminRoute>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <SocketProvider>
        <ToastProvider>
          <AppRoutes />
        </ToastProvider>
      </SocketProvider>
    </AuthProvider>
  );
}
